import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Modal, Spin, message } from 'antd';
import * as Icons from '@ant-design/icons';
import { BarcodeScan } from '@shared/components/barcode-scan';
import { useBackgroundNavigate } from '@shared/hooks';
import { BranchInspectionsService } from '../services';
import { useBranchInspectionById } from '../hooks';
import { IScanItem } from '../interfaces';
import { ScanRow } from '../components';
import {
  BarcodeContainer,
  BarcodeIcon,
  Btn,
  Card,
  Content,
  EmptyIcon,
  EmptyState,
  EmptySubtitle,
  EmptyTitle,
  Header,
  HeaderButtons,
  HeaderLeft,
  HeaderTitle,
  InfoContent,
  InfoExtra,
  InfoIcon,
  InfoItem,
  InfoLabel,
  InfoSection,
  InfoTitle,
  InfoValue,
  LastScanned,
  LastScannedLabel,
  LastScannedValue,
  LeftPanel,
  Note,
  PageContainer,
  RightHeader,
  RightPanel,
  RightSubtitle,
  RightTitle,
  ScanFooter,
  ScanFooterContent,
  ScanFooterLabel,
  ScanFooterValue,
  ScanList,
  StatCard,
  StatGrid,
  StatIcon,
  StatLabel,
  StatValue,
  TextInput,
} from '../styled';

export const BranchInspectionScan: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useBackgroundNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [scanData, setScanData] = useState<IScanItem[]>([]);
  const [lastScanned, setLastScanned] = useState('');
  const [initialLoading, setInitialLoading] = useState(true);
  const [scanQueue, setScanQueue] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);

  const { data: inspectionData, isFetching: inspectionLoading } = useBranchInspectionById(id);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [scanData]);

  useEffect(() => {
    const loadScans = async () => {
      setInitialLoading(true);
      const result = await BranchInspectionsService.getScans(id!);
      if (result.status === 200) {
        setScanData(result.data.map((scan) => ({ ...scan, status: 'success' as const })));
      } else {
        message.error(result.data as string);
      }
      setInitialLoading(false);
    };
    loadScans();
  }, [id]);

  // Process scan queue
  useEffect(() => {
    const processScanQueue = async () => {
      if (scanQueue.length === 0 || isProcessing) return;
      setIsProcessing(true);
      const currentTrackCode = scanQueue[0];
      const tempId = Date.now();

      const newScan: IScanItem = {
        id: tempId,
        inspectionId: parseInt(id!, 10),
        declarationId: 0,
        trackCode: currentTrackCode,
        trendyol: 0,
        barcode: currentTrackCode,
        partnerId: null,
        scannedAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        status: 'pending',
      };
      setScanData((prev) => [newScan, ...prev]);

      const result = await BranchInspectionsService.scanTrackingCode(id!, currentTrackCode);
      setScanData((prev) =>
        prev.map((item) => {
          if (item.id !== tempId) return item;
          if (result.status === 200) return { ...result.data, status: 'success' as const };
          return { ...item, status: 'error' as const, errorMessage: result.data as string };
        }),
      );
      setScanQueue((prev) => prev.slice(1));
      setIsProcessing(false);
    };
    processScanQueue();
  }, [scanQueue, isProcessing, id]);

  const handleScan = useCallback(() => {
    const value = inputRef.current?.value.trim() ?? '';
    if (!value) return;
    setScanQueue((prev) => [...prev, value]);
    setLastScanned(value);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }, []);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleScan();
  };

  const handleReset = () => {
    Modal.confirm({
      title: 'Diqqət',
      content: 'Bütün scan məlumatları silinəcək və proses yenidən başlayacaq. Əminsiniz?',
      okText: 'Bəli',
      cancelText: 'Xeyr',
      onOk: () => {
        setScanData([]);
        if (inputRef.current) {
          inputRef.current.value = '';
        }
        setLastScanned('');
        message.success('Məlumatlar sıfırlandı');
      },
    });
  };

  const handleFinish = () => {
    if (scanData.length === 0) {
      message.warning('Heç bir bağlama scan olunmayıb!');
      return;
    }
    Modal.confirm({
      title: 'Yoxlanışı bitirmək',
      content: `Yoxlanış tamamlanacaq. Cəmi ${scanData.length} bağlama scan olundu. Əminsiniz?`,
      okText: 'Bitir',
      cancelText: 'Ləğv et',
      onOk: async () => {
        const result = await BranchInspectionsService.changeStatus(id!, 157);
        if (result.status === 200) {
          message.success('Yoxlanış tamamlandı!');
          navigate(-1);
        } else {
          message.error(result.data as string);
        }
      },
    });
  };

  const handleRetry = useCallback((scan: IScanItem) => {
    setScanData((prev) => prev.filter((item) => item.id !== scan.id));
    setScanQueue((prev) => [...prev, scan.trackCode?.toString() || '']);
  }, []);

  const { successCount, errorCount } = useMemo(
    () =>
      scanData.reduce(
        (acc, item) => {
          if (item.status === 'success') acc.successCount += 1;
          else if (item.status === 'error') acc.errorCount += 1;
          return acc;
        },
        { successCount: 0, errorCount: 0 },
      ),
    [scanData],
  );

  const canScan = inspectionData?.stateId === 155 || inspectionData?.stateId === 156;

  if (initialLoading || inspectionLoading) {
    return (
      <PageContainer>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 'calc(100vh - 64px)' }}>
          <Spin size="large" />
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Header>
        <HeaderLeft>
          <Btn onClick={() => navigate(-1)}>
            <Icons.ArrowLeftOutlined />
          </Btn>
          <HeaderTitle>
            #{inspectionData?.id} - {inspectionData?.branch.name}
          </HeaderTitle>
        </HeaderLeft>
        <HeaderButtons>
          <Btn onClick={handleReset} disabled={scanData.length === 0}>
            <Icons.ReloadOutlined />
            Sıfırla
          </Btn>
          <Btn $primary onClick={handleFinish}>
            <Icons.CheckCircleOutlined />
            Yoxlanışı bitir
          </Btn>
        </HeaderButtons>
      </Header>

      <Content>
        <LeftPanel>
          <Card>
            <BarcodeContainer>
              <BarcodeIcon>
                <BarcodeScan barcode={lastScanned || 'Scan et'} />
              </BarcodeIcon>

              <InfoSection>
                <InfoTitle>Yoxlanış məlumatları</InfoTitle>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '12px' }}>
                  <InfoItem style={{ marginBottom: 0 }}>
                    <InfoIcon $color="#fa8c16">
                      <Icons.ClockCircleOutlined style={{ fontSize: 16 }} />
                    </InfoIcon>
                    <InfoContent>
                      <InfoLabel>Son tarix</InfoLabel>
                      <InfoValue>{inspectionData?.deadline.value}</InfoValue>
                      <InfoExtra color="#fa8c16">{inspectionData?.deadline.left} qalıb</InfoExtra>
                    </InfoContent>
                  </InfoItem>
                  <InfoItem style={{ marginBottom: 0 }}>
                    <InfoIcon $color="#1890ff">
                      <Icons.CalendarOutlined style={{ fontSize: 16 }} />
                    </InfoIcon>
                    <InfoContent>
                      <InfoLabel>Yaradılıb</InfoLabel>
                      <InfoValue>{inspectionData?.createdAt}</InfoValue>
                    </InfoContent>
                  </InfoItem>
                </div>
                <InfoItem>
                  <InfoIcon $color="#595959">
                    <Icons.FileTextOutlined style={{ fontSize: 16 }} />
                  </InfoIcon>
                  <InfoContent>
                    <InfoLabel>Qeyd</InfoLabel>
                    <InfoValue>{inspectionData?.note || 'Qeyd olunmayıb'}</InfoValue>
                  </InfoContent>
                </InfoItem>
              </InfoSection>
            </BarcodeContainer>

            <div>
              <TextInput
                ref={inputRef}
                type="text"
                onKeyPress={handleKeyPress}
                placeholder={canScan ? 'İzləmə kodunu daxil edin və ya scan edin...' : 'Yoxlanış statusu düzgün deyil'}
                autoFocus
                disabled={!canScan}
              />
              <Btn $primary onClick={handleScan} disabled={!canScan} style={{ width: '100%', marginTop: 12, justifyContent: 'center' }}>
                Scan et
              </Btn>

              {!canScan && (
                <div style={{ marginTop: 12, padding: 12, backgroundColor: '#fff1f0', border: '1px solid #ffa39e', borderRadius: 6 }}>
                  <p style={{ margin: 0, fontSize: 14, color: '#cf1322' }}>
                    <Icons.ExclamationCircleOutlined style={{ marginRight: 8 }} />
                    Yoxlanış statusu düzgün deyil. Yalnız state_id 155 və ya 156 olan yoxlanışlarda scan edilə bilər.
                  </p>
                </div>
              )}

              {lastScanned && (
                <LastScanned>
                  <LastScannedLabel>Son oxudulan:</LastScannedLabel>
                  <LastScannedValue>{lastScanned}</LastScannedValue>
                </LastScanned>
              )}
            </div>
          </Card>

          <StatCard>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <StatLabel>Cəmi scan olunub</StatLabel>
                <StatValue>{scanData.length}</StatValue>
              </div>
              <StatIcon $bgColor="#e6f7ff" $color="#1890ff">
                <Icons.InboxOutlined />
              </StatIcon>
            </div>
          </StatCard>

          <StatGrid>
            <StatCard>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <StatLabel>Uğurlu</StatLabel>
                  <StatValue $color="#52c41a">{successCount}</StatValue>
                </div>
                <StatIcon $bgColor="#f6ffed" $color="#52c41a">
                  <Icons.CheckCircleOutlined />
                </StatIcon>
              </div>
            </StatCard>

            <StatCard>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <StatLabel>Xəta</StatLabel>
                  <StatValue $color="#ff4d4f">{errorCount}</StatValue>
                </div>
                <StatIcon $bgColor="#fff1f0" $color="#ff4d4f">
                  <Icons.CloseCircleOutlined />
                </StatIcon>
              </div>
            </StatCard>
          </StatGrid>

          <Note>
            <p>
              <strong>Qeyd:</strong> Bağlamaları scan edərkən yalnız Findex və ya United-ə məxsus barkodları oxuya bilərsiz. Oxunmayan barkodları əl ilə yuxarıdakı inputa daxil edib, enter düyməsini
              sıxmaqla əlavə edə bilərsiz.
            </p>
          </Note>
        </LeftPanel>

        <RightPanel>
          <RightHeader>
            <RightTitle>Scan olunmuş bağlamalar</RightTitle>
            <RightSubtitle>Ən yeni bağlamalar yuxarıda göstərilir</RightSubtitle>
          </RightHeader>

          {scanData.length === 0 ? (
            <EmptyState>
              <EmptyIcon>
                <Icons.InboxOutlined />
              </EmptyIcon>
              <EmptyTitle>Hələ heç bir bağlama scan olunmayıb</EmptyTitle>
              <EmptySubtitle>Bağlamaları scan etməyə başlayın</EmptySubtitle>
            </EmptyState>
          ) : (
            <ScanList ref={scrollRef}>
              {scanData.map((item) => (
                <ScanRow key={item.id} item={item} onRetry={handleRetry} />
              ))}
            </ScanList>
          )}

          {scanData.length > 0 && (
            <ScanFooter>
              <ScanFooterContent>
                <ScanFooterLabel>Scroll edin və daha çox bağlama scan edin</ScanFooterLabel>
                <ScanFooterValue>Cəmi: {scanData.length}</ScanFooterValue>
              </ScanFooterContent>
            </ScanFooter>
          )}
        </RightPanel>
      </Content>
    </PageContainer>
  );
};
